"use client";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Notification/NotificationSignalHeaderLabel";

import { NextImage } from "@/src/components/NextImage";

const I18n = I18nUtil.createScoped(Translations);

export interface NotificationSignalHeaderLabelSignalItem {
  num: number;
  imageUrl?: string;
}

/**
 * Single holdings signal chip: avatar + label (Figma 81033:18539).
 */
export interface NotificationSignalHeaderLabelItem {
  label: string;
  signals: NotificationSignalHeaderLabelSignalItem[];
}

export interface NotificationSignalHeaderLabelProps {
  items: NotificationSignalHeaderLabelItem[];
  className?: string;
}

function LabelPill({ signals, label }: NotificationSignalHeaderLabelItem) {
  const count = signals?.length ?? 0;

  return (
    <span
      className={cn(
        "inline-flex max-w-full min-w-0 items-center gap-1 rounded-2",
        "bg-background_white_overlay5",
        "py-1 px-1.5",
      )}
    >
      <span className="truncate body_medium_s text-content_secondary">
        {label}
      </span>
      {count > 0 ? (
        <span className="shrink-0 body_s text-content_secondary">
          {I18n.t("x")}
          {count}
        </span>
      ) : null}
      <span className="inline-flex items-center">
        {signals?.slice(0, 2)?.map((signal, index) => (
          <NextImage
            key={`${label}-${index}`}
            src={signal.imageUrl}
            alt={`${label} signal ${signal.num}`}
            width={16}
            height={16}
            className={cn(
              "size-4 shrink-0 rounded-full border border-background_solid_neutral_primary object-cover",
              index > 0 && "-ml-1",
            )}
          />
        ))}
      </span>
    </span>
  );
}

/**
 * Horizontal row of signal labels (image + text per item). Pass `items` as an array.
 */
export function NotificationSignalHeaderLabel({
  items,
  className,
}: NotificationSignalHeaderLabelProps) {
  if (!items.length) {
    return null;
  }

  return (
    <div
      className={cn("flex flex-wrap gap-2", className)}
      data-openreplay-label="Notification signal header labels"
    >
      {items.map((item, index) => (
        <LabelPill
          key={`${index}-${item.label}`}
          label={item.label}
          signals={item.signals}
        />
      ))}
    </div>
  );
}
